/**
 * Console Provider - Fallback error tracking
 *
 * Used when no external provider is configured or available.
 * Logs errors to console with structured formatting.
 */

import type {
  ErrorTrackingProvider,
  ProviderConfig,
  ErrorContext,
  LogLevel,
} from '../types';

export class ConsoleProvider implements ErrorTrackingProvider {
  private config: ProviderConfig | null = null;
  private userId: string | null = null;

  init(config: ProviderConfig): void {
    this.config = config;
    console.log('[ErrorTracking] Console provider initialized', {
      serviceName: config.serviceName,
      environment: config.environment,
    });
  }

  captureError(error: Error | string, context?: ErrorContext): void {
    const errorObj = typeof error === 'string' ? new Error(error) : error;

    console.error('[ErrorTracking] Error captured:', {
      message: errorObj.message,
      stack: errorObj.stack,
      context,
      userId: this.userId,
      timestamp: new Date().toISOString(),
    });
  }

  captureMessage(
    message: string,
    level: LogLevel,
    context?: ErrorContext
  ): void {
    const logFn =
      level === 'error'
        ? console.error
        : level === 'warning'
          ? console.warn
          : console.log;

    logFn(`[ErrorTracking] ${level.toUpperCase()}:`, {
      message,
      context,
      userId: this.userId,
      timestamp: new Date().toISOString(),
    });
  }

  setUser(userId: string | null): void {
    this.userId = userId;
    console.log('[ErrorTracking] User context:', userId ? 'set' : 'cleared');
  }

  addBreadcrumb(
    message: string,
    category: string,
    data?: Record<string, unknown>
  ): void {
    console.log('[ErrorTracking] Breadcrumb:', { category, message, data });
  }

  isActive(): boolean {
    return this.config !== null;
  }

  async flush(): Promise<void> {
    // console writes are synchronous; nothing to drain.
  }
}
